import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Badge,
  Box,
  Button,
  Divider,
  Heading,
  HStack,
  Input,
  List,
  ListItem,
  OrderedList,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { ENDPOINTS } from 'utils/Constants';
import { currentUser } from 'utils/CurrentUser';
import { notify } from 'components/ToastNotification';

export default function PersonalCourseLesson() {
  const navigate = useNavigate();
  const { courseId, moduleId, lessonId } = useParams();
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [practicePrompt, setPracticePrompt] = useState('');
  const [userAnswer, setUserAnswer] = useState('');
  const [practiceResult, setPracticeResult] = useState(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [practiceType, setPracticeType] = useState('quiz');

  const cardBg = useColorModeValue('white', 'gray.800');
  const pageBg = useColorModeValue('#F3F7FF', '#0B1220');
  const mutedText = useColorModeValue('gray.600', 'gray.300');
  const accentBg = useColorModeValue('blue.50', 'blue.900');
  const heroBg = useColorModeValue(
    'linear-gradient(120deg, #0EA5E9 0%, #2563EB 50%, #1E3A8A 100%)',
    'linear-gradient(120deg, #0284C7 0%, #1D4ED8 45%, #0F172A 100%)'
  );
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await axios.get(`${ENDPOINTS.GET_PERSONAL_COURSE}${courseId}`, {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        });

        if (!response?.data?.success) {
          notify(response?.data?.output || 'Failed to fetch lesson', 'error');
          return;
        }

        const fetchedCourse = response.data.data.course;
        setCourse(fetchedCourse);

        const foundModule = (fetchedCourse.coursePack?.modules || []).find((item) => item.id === moduleId);
        const foundLesson = foundModule?.lessons?.find((item) => item.id === lessonId);
        setModule(foundModule || null);
        setLesson(foundLesson || null);

        const firstQuiz = foundLesson?.practice?.quiz?.[0];
        setPracticePrompt(firstQuiz?.question || foundLesson?.practice?.drills?.[0]?.task || 'Practice this lesson in your own words.');
      } catch (error) {
        if (error?.response?.status === 403) {
          setUpgradeRequired(true);
          return;
        }
        notify(error?.response?.data?.output || 'Failed to fetch lesson', 'error');
      }
    };

    fetchCourse();
  }, [courseId, lessonId, moduleId]);

  const markComplete = async () => {
    try {
      const response = await axios.post(
        `${ENDPOINTS.UPDATE_PERSONAL_COURSE_PROGRESS}${courseId}/progress`,
        { moduleId, lessonId, markComplete: true, score: 100 },
        { headers: { Authorization: `Bearer ${currentUser.token}` } }
      );

      if (!response?.data?.success) {
        notify(response?.data?.output || 'Failed to mark complete', 'error');
        return;
      }

      notify('Lesson marked complete', 'success');
    } catch (error) {
      notify(error?.response?.data?.output || 'Failed to mark complete', 'error');
    }
  };

  const submitPractice = async () => {
    if (userAnswer.trim().length < 10) {
      notify('Please add a longer answer before evaluation.', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${ENDPOINTS.SUBMIT_PERSONAL_COURSE_PRACTICE}${courseId}/practice`,
        { moduleId, lessonId, prompt: practicePrompt, userAnswer },
        { headers: { Authorization: `Bearer ${currentUser.token}` } }
      );

      if (!response?.data?.success) {
        notify(response?.data?.output || 'Practice submission failed', 'error');
        return;
      }

      setPracticeResult(response.data.data);
      notify('Practice evaluated', 'success');
    } catch (error) {
      if (error?.response?.status === 403) {
        setUpgradeRequired(true);
        return;
      }
      notify(error?.response?.data?.output || 'Practice submission failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const nextLesson = useMemo(() => {
    if (!course?.coursePack?.modules) return null;
    for (const moduleItem of course.coursePack.modules) {
      for (const lessonItem of moduleItem.lessons || []) {
        if (moduleItem.id === moduleId && lessonItem.id === lessonId) continue;
        return { moduleId: moduleItem.id, lessonId: lessonItem.id };
      }
    }
    return null;
  }, [course, moduleId, lessonId]);

  const quizItems = lesson?.practice?.quiz || [];
  const flashcardItems = lesson?.practice?.flashcards || [];
  const drillItems = lesson?.practice?.drills || [];

  const selectPrompt = (type, item) => {
    setPracticeType(type);
    if (type === 'quiz') {
      const optionsText = (item.options || []).map((option, index) => `${String.fromCharCode(65 + index)}. ${option}`).join(' | ');
      setPracticePrompt(`${item.question}${optionsText ? `\nOptions: ${optionsText}` : ''}`);
      return;
    }
    if (type === 'flashcard') {
      setPracticePrompt(`Explain this flashcard in your own words:\n${item.front}`);
      return;
    }
    setPracticePrompt(item.task);
  };

  if (upgradeRequired) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px="20px" bg={pageBg} minH="100vh">
        <Box bg={cardBg} borderRadius="20px" p="20px" border="1px solid" borderColor={borderColor}>
          <Heading fontSize="2xl">Upgrade Required</Heading>
          <Text mt="2" color={mutedText}>This feature is available only on the Premium plan.</Text>
          <Button mt="4" variant="darkBrand" onClick={() => navigate('/admin/profile')}>View Plans</Button>
        </Box>
      </Box>
    );
  }

  if (!lesson || !module) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px="20px" bg={pageBg} minH="100vh">
        <Box bg={cardBg} borderRadius="20px" p="20px" border="1px solid" borderColor={borderColor}>
          <Text>Loading lesson...</Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={{ base: '14px', md: '20px' }} bg={pageBg} minH="100vh">
      <Box
        borderRadius="22px"
        p={{ base: '16px', md: '22px' }}
        mb="20px"
        color="white"
        bg={heroBg}
        boxShadow="xl"
      >
        <Badge bg="whiteAlpha.300" color="white" px="10px" py="4px" borderRadius="full">{module.title}</Badge>
        <Heading mt="2" fontSize={{ base: 'xl', md: '3xl' }}>{lesson.title}</Heading>
        <Text mt="2" maxW="780px" opacity={0.95}>Learn, apply, and get instant AI feedback.</Text>
      </Box>

      <Box bg={cardBg} borderRadius="20px" p="20px" boxShadow="md" mb="20px" border="1px solid" borderColor={borderColor}>
        <Text fontWeight="700" mb="2">Learning Content</Text>
        <Text color={mutedText} whiteSpace="pre-wrap">{lesson.content}</Text>

        <Text mt="4" fontWeight="700">Key Points</Text>
        <OrderedList mt="2">
          {(lesson.keyPoints || []).map((item) => <ListItem key={item}>{item}</ListItem>)}
        </OrderedList>

        <Divider my="4" />
        <HStack justify="space-between" align="center" wrap="wrap">
          <Text fontWeight="700">Lesson Completion</Text>
          <Button variant="darkBrand" onClick={markComplete}>Mark complete</Button>
        </HStack>
      </Box>

      <Box bg={cardBg} borderRadius="20px" p="20px" boxShadow="md" border="1px solid" borderColor={borderColor}>
        <Heading fontSize="xl">Practice</Heading>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px" mt="3" mb="3">
          <Badge colorScheme="blue">Quizzes: {(lesson.practice?.quiz || []).length}</Badge>
          <Badge colorScheme="purple">Flashcards: {(lesson.practice?.flashcards || []).length}</Badge>
          <Badge colorScheme="orange">Drills: {(lesson.practice?.drills || []).length}</Badge>
        </SimpleGrid>

        <Text fontWeight="700" mt="2">1) Choose a practice item</Text>
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing="10px" mt="3">
          <Box p="10px" borderRadius="12px" border="1px solid" borderColor={borderColor}>
            <Text fontWeight="700" mb="2">Quiz</Text>
            <List spacing="6px">
              {quizItems.map((item) => (
                <ListItem key={item.id}>
                  <Button size="xs" variant="outline" onClick={() => selectPrompt('quiz', item)}>Use</Button>
                  <Text mt="1" fontSize="sm" color={mutedText}>{item.question}</Text>
                </ListItem>
              ))}
            </List>
          </Box>
          <Box p="10px" borderRadius="12px" border="1px solid" borderColor={borderColor}>
            <Text fontWeight="700" mb="2">Flashcards</Text>
            <List spacing="6px">
              {flashcardItems.map((item) => (
                <ListItem key={item.id}>
                  <Button size="xs" variant="outline" onClick={() => selectPrompt('flashcard', item)}>Use</Button>
                  <Text mt="1" fontSize="sm" color={mutedText}>Front: {item.front}</Text>
                  <Text mt="1" fontSize="xs" color={mutedText}>Expected: {item.back}</Text>
                </ListItem>
              ))}
            </List>
          </Box>
          <Box p="10px" borderRadius="12px" border="1px solid" borderColor={borderColor}>
            <Text fontWeight="700" mb="2">Drills</Text>
            <List spacing="6px">
              {drillItems.map((item) => (
                <ListItem key={item.id}>
                  <Button size="xs" variant="outline" onClick={() => selectPrompt('drill', item)}>Use</Button>
                  <Text mt="1" fontSize="sm" color={mutedText}>{item.task}</Text>
                </ListItem>
              ))}
            </List>
          </Box>
        </SimpleGrid>

        <Text fontWeight="700" mt="4">2) Answer and submit</Text>
        <Badge mt="2" colorScheme="blue">Selected: {practiceType}</Badge>
        <Text fontWeight="700">Prompt</Text>
        <Text color={mutedText} whiteSpace="pre-wrap">{practicePrompt}</Text>
        <Input mt="3" value={userAnswer} onChange={(event) => setUserAnswer(event.target.value)} placeholder="Type your answer" />
        <Button mt="3" colorScheme="blue" isLoading={loading} onClick={submitPractice}>Get feedback</Button>

        {practiceResult && (
          <Stack mt="4" spacing="8px" p="12px" borderRadius="12px" bg={accentBg} border="1px solid" borderColor={borderColor}>
            <Text><b>Immediate feedback</b></Text>
            <Text>Score: {practiceResult.attempt?.aiScore || practiceResult.progress?.latestScore || 0}</Text>
            <Progress value={practiceResult.attempt?.aiScore || practiceResult.progress?.latestScore || 0} borderRadius="full" />
            <Text>{practiceResult.attempt?.aiFeedback?.nextStep || 'Great effort. Keep going.'}</Text>
            {(practiceResult.attempt?.aiFeedback?.feedback || []).map((item) => (
              <Text key={item}>- {item}</Text>
            ))}

            <Button
              variant="darkBrand"
              onClick={() => {
                if (nextLesson) {
                  navigate(`/admin/personal-courses/${courseId}/lesson/${nextLesson.moduleId}/${nextLesson.lessonId}`);
                } else {
                  navigate('/admin/personal-courses');
                }
              }}
            >
              Next
            </Button>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
